import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "distributor", "export", "contact", "career"];

const TYPE_STYLES = {
  distributor: "bg-gold/15 text-gold border-gold/40",
  export: "bg-emerald/40 text-emerald-100 border-emerald-light/50",
  contact: "bg-white/10 text-zinc-300 border-white/20",
  career: "bg-maroon/40 text-red-100 border-maroon-light/50",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("waghe_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (tok = token, f = filter) => {
      if (!tok) return;
      setLoading(true);
      try {
        const res = await fetch(`${API}/inquiries${f !== "all" ? `?type=${f}` : ""}`, {
          headers: { Authorization: `Bearer ${tok}` },
          credentials: "include",
        });
        if (res.status === 401) {
          localStorage.removeItem("waghe_admin_token");
          setToken("");
          toast.error("Session expired. Please sign in again.");
          return;
        }
        const data = await res.json();
        setInquiries(data.inquiries || []);
      } catch {
        toast.error("Could not load inquiries.");
      } finally {
        setLoading(false);
      }
    },
    [token, filter]
  );

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const login = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "Login failed.";
        throw new Error(detail);
      }
      localStorage.setItem("waghe_admin_token", data.access_token);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user.email}`);
      setTimeout(() => load(data.access_token, "all"), 50);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("waghe_admin_token");
    setToken("");
    setInquiries([]);
    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
  };

  if (!token) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-ink px-5 pt-24 pb-16" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-coal border border-white/10 rounded-sm p-8 sm:p-10">
          <p className="font-display text-3xl text-cream tracking-tight">Waghe <span className="text-gold italic">&amp;</span> Sons</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-2 mb-8">Admin · Inquiry Desk</p>
          <form onSubmit={login} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-xs uppercase tracking-[0.16em] text-zinc-400">Email</Label>
              <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-black/50 border-white/10 text-cream rounded-sm h-12 focus-visible:ring-gold" placeholder="admin@waghefoods.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs uppercase tracking-[0.16em] text-zinc-400">Password</Label>
              <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-black/50 border-white/10 text-cream rounded-sm h-12 focus-visible:ring-gold" placeholder="••••••••" />
            </div>
            <button
              type="submit"
              disabled={authLoading}
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-6 py-4 rounded-sm transition-[background-color] duration-300 hover:bg-gold-light disabled:opacity-60"
            >
              {authLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {authLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-ink pt-32 pb-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-5 mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-overline text-gold">Inquiry Desk</p>
            <h1 className="mt-2 font-display text-4xl text-cream" data-testid="admin-title">All Enquiries <span className="text-zinc-600 text-2xl">({inquiries.length})</span></h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load()} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 text-xs uppercase tracking-[0.14em] px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors duration-300">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-maroon-light/50 text-red-200 text-xs uppercase tracking-[0.14em] px-5 py-3 rounded-sm hover:bg-maroon transition-colors duration-300">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-8" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`text-[11px] uppercase tracking-[0.16em] px-4 py-2 rounded-full border transition-colors duration-300 ${
                filter === f ? "bg-gold text-ink border-gold font-semibold" : "border-white/15 text-zinc-400 hover:border-gold/60 hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
        ) : inquiries.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-sm py-24 flex flex-col items-center gap-4 text-zinc-500" data-testid="admin-empty-state">
            <Inbox className="w-10 h-10" strokeWidth={1.2} />
            <p className="text-sm">No enquiries yet in this category.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-inquiry-list">
            {inquiries.map((q) => (
              <div key={q.id} className="bg-coal border border-white/10 rounded-sm p-6 grid md:grid-cols-12 gap-4 hover:border-gold/30 transition-colors duration-300" data-testid={`admin-inquiry-${q.id}`}>
                <div className="md:col-span-3">
                  <span className={`inline-block text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border ${TYPE_STYLES[q.type] || TYPE_STYLES.contact}`}>{q.type}</span>
                  <p className="mt-3 text-xs text-zinc-500">{new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
                <div className="md:col-span-4">
                  <p className="font-display text-lg text-cream">{q.name}</p>
                  <p className="text-xs text-zinc-400 mt-1">{q.email} · {q.phone}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {[q.company, q.city, q.country].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className="md:col-span-5">
                  <p className="text-sm text-zinc-300 font-light leading-relaxed">{q.message || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
